import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { fetchJson, ApiError } from "@/lib/api";

// Phase 2a customer-accounts FR-2/FR-4/FR-5/FR-6/FR-13. Single source of
// truth for "who is signed in right now" in the customer SPA. The server
// owns the Devise cookie; the React side holds a derived `user` snapshot
// that's wiped on sign-out and refreshed on demand.
//
// We deliberately keep this state minimal — the components that need PII
// (account page, checkout) call /api/v1/account/profile themselves via
// react-query rather than caching the profile in this context. That
// avoids stale name/phone re-renders when the profile changes elsewhere.

export interface AuthUser {
  id: number;
  email: string;
  confirmation_pending?: boolean;
  unconfirmed_email?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<AuthUser>;
  signUp(payload: SignUpPayload): Promise<AuthUser>;
  signOut(): Promise<void>;
  consumeMagicLink(token: string): Promise<AuthUser>;
  setUser(user: AuthUser | null): void;
}

export interface SignUpPayload {
  email: string;
  password: string;
  password_confirmation: string;
  first_name: string;
  last_name: string;
  phone: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SIGNED_IN_USER_TAG = "data-current-user";

// The server-rendered HTML host (HomeController/CustomerAppController)
// can embed a `<meta name="jdr-current-user" content='{"id":...}'>` tag
// to seed the user without a /me round-trip on first paint. The seed
// is best-effort; if it's missing we resolve to `null`.
function readSeedUser(): AuthUser | null {
  const meta = document.querySelector<HTMLMetaElement>("meta[name='jdr-current-user']");
  if (!meta?.content) return null;
  try {
    const parsed = JSON.parse(meta.content) as AuthUser;
    if (parsed && typeof parsed.id === "number") {
      meta.setAttribute(SIGNED_IN_USER_TAG, "consumed");
      return parsed;
    }
  } catch {
    // ignore malformed seeds
  }
  return null;
}

interface AuthApiResponse {
  user: AuthUser;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(() => readSeedUser());
  const [loading, setLoading] = useState(false);

  // FR-15: on email-change confirmation the server clears the session.
  // We listen for the BroadcastChannel "jdr-auth" emitted by the
  // server-rendered confirmation landing page so the rest of the SPA
  // can react to "your other tab signed out".
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel("jdr-auth");
    const handler = (e: MessageEvent) => {
      if (e.data === "signed-out") setUser(null);
    };
    ch.addEventListener("message", handler);
    return () => {
      ch.removeEventListener("message", handler);
      ch.close();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    setLoading(true);
    try {
      const res = await fetchJson<AuthApiResponse>("/api/v1/auth/signin", {
        method: "POST",
        body: { email, password }
      });
      setUser(res.user);
      return res.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (payload: SignUpPayload): Promise<AuthUser> => {
    setLoading(true);
    try {
      const res = await fetchJson<AuthApiResponse>("/api/v1/auth/signup", {
        method: "POST",
        body: {
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
          profile: {
            first_name: payload.first_name,
            last_name: payload.last_name,
            phone: payload.phone
          }
        }
      });
      setUser(res.user);
      return res.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      await fetchJson("/api/v1/auth/signout", { method: "DELETE" });
    } catch (err) {
      // 401 from signout is "already signed out" — treat as success
      if (!(err instanceof ApiError) || err.status !== 401) throw err;
    } finally {
      setUser(null);
      setLoading(false);
    }
  }, []);

  const consumeMagicLink = useCallback(async (token: string): Promise<AuthUser> => {
    setLoading(true);
    try {
      const res = await fetchJson<AuthApiResponse>("/api/v1/auth/magic_link/consume", {
        method: "POST",
        body: { token }
      });
      setUser(res.user);
      return res.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signIn, signUp, signOut, consumeMagicLink, setUser }),
    [user, loading, signIn, signUp, signOut, consumeMagicLink]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth() must be used inside <AuthProvider>");
  }
  return ctx;
}
